import React, { JSX, useRef, useState } from "react";
import { Link } from "react-router";
import { useIsSmallScreen } from "../App";
import { Canticle } from "../chants/Canticle";
import { Chant } from "../chants/Chant";
import { OrdPsalm } from "../chants/OrdPsalm";
import { Psalm } from "../chants/Psalm";
import { EBPlayButton } from "../chants/audio/EBPlayButton";
import { PlayableTitle } from "../chants/audio/PlayableTitle";
import { TonePlayer } from "../chants/audio/TonePlayer";
import { EBChant } from "../chants/eb-chant/EBChant";
import { Footer } from "../pages/pray/Footer";
import { Heading } from "../pages/pray/Heading";
import { LegacyRubric } from "../pages/pray/LegacyRubric";
import { LoadingScreen } from "../pages/pray/LoadingScreen";
import {
    NewHymn,
    getHymnAutoTweak,
    getHymnVerseGabc,
} from "../pages/pray/NewHymn";
import { OfficeHymn } from "../pages/pray/OfficeHymn";
import { Rubric } from "../pages/pray/Rubric";
import { Scripture } from "../pages/pray/Scripture";
import { Spacer } from "../pages/pray/Spacer";
import { Subheading } from "../pages/pray/Subheading";
import { TextBlock } from "../pages/pray/TextBlock";
import { Padding } from "../shared-components/Padding";
import { PAGE_BACKGROUND } from "../tokens";
import { getBooklet } from "./booklet";
import { LiturgyItem } from "./types";

export const LiturgyViewer: React.FC<{
    items: LiturgyItem[];
    label?: string;
    superTitle?: string;
    title?: string;
    forceRender?: boolean;
}> = (props) => {
    const { items, label, superTitle, title, forceRender } = props;

    const [modals, setModals] = useState<{ [id: string]: JSX.Element }>({});
    const addModal = (id: string, modal: JSX.Element) => {
        modals[id] = modal;
        setModals(modals);
    };

    const [loadingBookletState, setLoadingBookletState] = useState<
        "initial" | "loading" | "loaded"
    >("initial");

    const isSmallScreen = useIsSmallScreen();

    const topElement = useRef<HTMLDivElement>(null as any);
    const bottomElement = useRef<HTMLDivElement>(null as any);

    return (
        <>
            <div ref={topElement}></div>
            {items
                .map((item) => {
                    switch (item.type) {
                        case "heading":
                            return <Heading day={item.day} hour={item.hour} />;

                        case "rubric":
                            return (
                                <Rubric
                                    content={item.content}
                                    settingsPanelId={item.settingsPanelId}
                                />
                            );

                        case "subheading":
                            return <Subheading content={item.content} />;

                        case "text":
                            return (
                                <TextBlock
                                    content={item.content}
                                    dropCap={item.dropCap}
                                />
                            );

                        case "spacer":
                            return <Spacer big={item.big} />;

                        case "chant":
                            return (
                                <Chant
                                    boldMode={item.boldMode}
                                    pointedText={item.pointedText}
                                    rubric={item.rubric}
                                    dropCap={false}
                                    tone={item.tone}
                                />
                            );

                        case "tone chant":
                            return (
                                <>
                                    <PlayableTitle
                                        id={item.title
                                            .replace(/ /g, "-")
                                            .toLocaleLowerCase()}
                                        title={item.title}
                                        player={
                                            <TonePlayer
                                                tones={[item.tone]}
                                                selectedTone={item.tone}
                                                id={item.title
                                                    .replace(/ /g, "-")
                                                    .toLocaleLowerCase()}
                                                onSetModal={addModal}
                                            />
                                        }
                                    />
                                    <Chant
                                        boldMode={item.boldMode}
                                        pointedText={item.pointedText}
                                        rubric={item.rubric}
                                        dropCap={true}
                                        tone={item.tone}
                                    />
                                </>
                            );

                        case "gabc chant":
                            return (
                                <>
                                    <PlayableTitle
                                        id={item.title
                                            .replace(/ /g, "-")
                                            .toLocaleLowerCase()}
                                        title={item.title}
                                        player={
                                            <div
                                                style={{
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    gap: 4,
                                                }}
                                            >
                                                {item.playButtons.map((pb) => {
                                                    return (
                                                        <EBPlayButton
                                                            gabc={pb.gabc}
                                                            title={pb.title}
                                                            onlyIntone={
                                                                pb.intoneOnly
                                                            }
                                                            tone={item.tone}
                                                            tweak={0}
                                                        />
                                                    );
                                                })}
                                            </div>
                                        }
                                    />
                                    <Padding onlySides>
                                        <EBChant
                                            gabc={item.gabc}
                                            dropCap={item.dropCap}
                                            tone={item.tone}
                                        />
                                    </Padding>
                                </>
                            );

                        case "minimal gabc chant":
                            return (
                                <Padding onlySides>
                                    <EBChant
                                        gabc={item.gabc}
                                        dropCap={item.dropCap}
                                        tone={item.tone}
                                    />
                                </Padding>
                            );

                        case "canticle":
                            return (
                                <Canticle
                                    id={item.id}
                                    day={item.day}
                                    tone={item.tone}
                                />
                            );

                        case "office hymn":
                            return (
                                <OfficeHymn day={item.day} hour={item.hour} />
                            );

                        case "new hymn":
                            return (
                                <>
                                    <PlayableTitle
                                        id={item.hymn.title
                                            .replace(/ /g, "-")
                                            .toLocaleLowerCase()}
                                        title={item.hymn.title}
                                        player={
                                            <div
                                                style={{
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    gap: 4,
                                                }}
                                            >
                                                <EBPlayButton
                                                    gabc={getHymnVerseGabc(
                                                        item.hymn.tune,
                                                        item.hymn.text.split(
                                                            "\n\n"
                                                        )[0]
                                                    )}
                                                    title="Intone"
                                                    onlyIntone
                                                    tone="1"
                                                    tweak={getHymnAutoTweak(
                                                        item.hymn.tune
                                                    )}
                                                />
                                                <EBPlayButton
                                                    gabc={getHymnVerseGabc(
                                                        item.hymn.tune,
                                                        item.hymn.text.split(
                                                            "\n\n"
                                                        )[0]
                                                    )}
                                                    title="Play verse"
                                                    tone="1"
                                                    tweak={getHymnAutoTweak(
                                                        item.hymn.tune
                                                    )}
                                                />
                                            </div>
                                        }
                                    />
                                    <Padding onlySides>
                                        <NewHymn hymn={item.hymn} />
                                    </Padding>
                                </>
                            );

                        case "loading":
                            return <LoadingScreen />;

                        case "psalm":
                            return <Psalm psalmRef={item.psalmRef} />;

                        case "ord psalm":
                            return (
                                <OrdPsalm
                                    title={item.title}
                                    sections={item.sections}
                                    psalmNumber={item.psalmNumber}
                                />
                            );

                        case "scripture":
                            return (
                                <Scripture
                                    title={item.title}
                                    content={item.content}
                                    hasBeginneth={item.hasBeginneth}
                                />
                            );

                        case "litany rubric":
                            return item.day.label === "ash-wednesday" ? (
                                <>
                                    <Spacer />
                                    <LegacyRubric>
                                        <strong>ASH WEDNESDAY</strong>
                                        <br />
                                        It is appropriate to conclude with{" "}
                                        <strong
                                            style={{
                                                textDecoration: "underline",
                                            }}
                                        >
                                            <Link to="/pray/litany">
                                                The&nbsp;Litany
                                            </Link>
                                        </strong>{" "}
                                        and{" "}
                                        <strong
                                            style={{
                                                textDecoration: "underline",
                                            }}
                                        >
                                            <Link to="/pray/commination">
                                                The&nbsp;Commination
                                            </Link>
                                        </strong>
                                        .
                                    </LegacyRubric>
                                    <Spacer />
                                </>
                            ) : (
                                <LegacyRubric>
                                    On Sundays, Wednesdays, and Fridays, it is
                                    appropriate to conclude Mattins with{" "}
                                    <strong
                                        style={{ textDecoration: "underline" }}
                                    >
                                        <Link to="/pray/litany">
                                            The&nbsp;Litany
                                        </Link>
                                    </strong>
                                    .
                                </LegacyRubric>
                            );

                        case "footer":
                            return <Footer day={item.day} hour={item.hour} />;

                        case "picker":
                            return (
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        justifyContent: "center",
                                    }}
                                >
                                    <div
                                        style={{
                                            backgroundColor: PAGE_BACKGROUND,
                                            display: "flex",
                                            flexDirection: "row",
                                            padding: 2,
                                            borderRadius: 4,
                                        }}
                                    >
                                        {item.options.map((option) => {
                                            return (
                                                <div
                                                    style={{
                                                        padding: 4,
                                                        paddingLeft:
                                                            isSmallScreen
                                                                ? 6
                                                                : 12,
                                                        paddingRight:
                                                            isSmallScreen
                                                                ? 6
                                                                : 12,
                                                        backgroundColor:
                                                            item.selectedOption ===
                                                            option
                                                                ? "white"
                                                                : "transparent",
                                                        borderRadius: 2,
                                                        fontSize: "90%",
                                                        fontWeight: "bold",
                                                        color: "black",
                                                        cursor: "pointer",
                                                    }}
                                                    onClick={() => {
                                                        item.onChange(option);
                                                    }}
                                                >
                                                    {option}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                    }
                })
                .map((element, index) => {
                    return (
                        <div
                            className="liturgy-item"
                            key={`${index} - ${JSON.stringify(items[index])}`}
                        >
                            {element}
                        </div>
                    );
                })}
            {Object.values(modals)}
            {superTitle && title && label ? (
                <div
                    style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "center",
                        pointerEvents:
                            loadingBookletState === "initial" ? "all" : "none",
                        opacity: loadingBookletState === "initial" ? 1 : 0.75,
                    }}
                >
                    <div
                        onClick={async () => {
                            if (loadingBookletState === "initial") {
                                setLoadingBookletState("loading");

                                console.log("Beginning");

                                const scrollArea =
                                    document.getElementsByClassName(
                                        "app-scroll-area"
                                    )[0];

                                if (!scrollArea) {
                                    return;
                                }

                                let prevScrollTop = -100;

                                topElement.current.scrollIntoView();

                                const scrollThenGetBooklet = async () => {
                                    if (
                                        scrollArea.scrollTop === prevScrollTop
                                    ) {
                                        await getBooklet(
                                            superTitle,
                                            title,
                                            label
                                        );

                                        setLoadingBookletState("loaded");
                                    } else {
                                        prevScrollTop = scrollArea.scrollTop;
                                        scrollArea.scrollTo({
                                            top: scrollArea.scrollTop + 100,
                                        });
                                        setTimeout(() => {
                                            scrollThenGetBooklet();
                                        }, 50);
                                    }
                                };

                                scrollThenGetBooklet();
                            }
                        }}
                        className="new-button"
                    >
                        {loadingBookletState === "loading"
                            ? "Loading..."
                            : loadingBookletState === "loaded"
                            ? "Downloaded booklet"
                            : "Download booklet"}
                    </div>
                </div>
            ) : (
                <></>
            )}
            <div ref={bottomElement} style={{ paddingBottom: 32 }}></div>
        </>
    );
};
